import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import {
  Home,
  Users,
  FileText,
  ShoppingCart,
  UserCog,
  Menu,
  X,
  Package,
  Building2,
  MessageSquare,
  ClipboardList,
} from "lucide-react";
import { Header } from "./header";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { HajocaHealth } from "./hajoca/health";
import { HelpWidget } from "./help-widget";
import { useInternalAccess } from "@/hooks/use-internal-access";
import { useOrgMetadata } from "@/hooks/use-org-metadata";
import { usePcHomeRoute, useWillCallEnabled } from "@/hooks/use-will-call";
import { useUser } from "@clerk/clerk-react";
import { UserButton, OrganizationSwitcher } from "@clerk/clerk-react";

interface LayoutProps {
  children: React.ReactNode;
  hideSidebar?: boolean;
  /** When true, layout fills exactly the viewport height with no page-level scroll. Children must manage their own overflow. */
  fillViewport?: boolean;
  /** Content rendered in the header bar (e.g. breadcrumb). On mobile, hamburger is prepended automatically. */
  headerContent?: React.ReactNode;
}

export function Layout({
  children,
  hideSidebar = false,
  fillViewport = false,
  headerContent,
}: LayoutProps) {
  const { t } = useTranslation();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isInternal } = useInternalAccess();
  const { metadata } = useOrgMetadata();
  const { isSignedIn } = useUser();

  const isProfitCenter = metadata?.type === "profit-center";
  const willCallEnabled = useWillCallEnabled();
  const pcHomeRoute = usePcHomeRoute();
  // Profit centers don't submit orders, so the contractor nav and the Hajoca
  // "order submission unavailable" banner don't apply to them.
  const homeHref = isProfitCenter ? pcHomeRoute : "/orders/new";

  const contractorNavItems = [
    { href: "/orders/new", icon: Home, label: t("components.layout.newOrder") },
    { href: "/customers", icon: Users, label: t("components.layout.customers") },
    { href: "/quotes", icon: FileText, label: t("components.layout.quotes") },
    { href: "/orders", icon: ShoppingCart, label: t("common.orders") },
    { href: "/admin", icon: UserCog, label: t("common.admin") },
  ];

  const pcNavItems = [
    // Will-call dashboard is behind the "will-call" flag during rollout
    ...(willCallEnabled
      ? [
          {
            href: "/pc/orders",
            icon: ClipboardList,
            label: t("pages.pcOrders.title"),
          },
        ]
      : []),
    {
      href: "/pc/orders/new",
      icon: Home,
      label: t("components.layout.newOrder"),
    },
    {
      href: "/pc/contractors",
      icon: Building2,
      label: t("components.layout.contractors"),
    },
  ];

  const navigationItems = isProfitCenter ? pcNavItems : contractorNavItems;

  const internalNavigationItems = [
    { href: "/internal/products", icon: Package, label: t("components.layout.products") },
    { href: "/internal/contractors", icon: Building2, label: t("components.layout.contractors") },
    { href: "/internal/sms-notifications", icon: MessageSquare, label: t("components.layout.smsNotifications") },
  ];

  const NavLink = ({
    href,
    icon: Icon,
    label,
    onClick,
  }: {
    href: string;
    icon: any;
    label: string;
    onClick?: () => void;
  }) => (
    <Link href={href}>
      <div
        className={`flex items-center space-x-3 px-4 py-3 rounded-md cursor-pointer touch-manipulation ${
          location === href
            ? "bg-black text-white font-medium"
            : "text-foreground hover:bg-accent"
        }`}
        onClick={onClick}
      >
        <Icon className="h-5 w-5" />
        <span className="text-sm">{label}</span>
      </div>
    </Link>
  );

  // Hamburger button for mobile header
  const hamburger = !hideSidebar ? (
    <Button
      variant="ghost"
      size="icon"
      className="lg:hidden h-9 w-9"
      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    >
      {mobileMenuOpen ? (
        <X className="h-5 w-5" />
      ) : (
        <Menu className="h-5 w-5" />
      )}
    </Button>
  ) : null;

  return (
    <div className="h-screen w-full overflow-hidden flex">
      {/* Desktop sidebar — full height, contains brand */}
      {!hideSidebar && (
        <aside className="hidden lg:flex lg:w-60 flex-col border-r bg-background flex-shrink-0">
          {/* Brand */}
          <div className="px-5 py-4 flex-shrink-0">
            <Link href={homeHref}>
              <span className="text-lg font-semibold cursor-pointer">
                {t("components.layout.onsemblePro")}
              </span>
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
            {navigationItems.map((item) => (
              <NavLink
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
              />
            ))}

            {isInternal && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t("components.layout.internal")}
                  </div>
                </div>
                {internalNavigationItems.map((item) => (
                  <NavLink
                    key={item.href}
                    href={item.href}
                    icon={item.icon}
                    label={item.label}
                  />
                ))}
              </>
            )}
          </nav>

          {/* User section — pinned to bottom */}
          {isSignedIn && (
            <div className="flex-shrink-0 border-t px-3 py-3 space-y-2.5">
              <div className="px-1">
                <OrganizationSwitcher
                  hidePersonal={true}
                  appearance={{
                    elements: {
                      rootBox: "w-full",
                      organizationSwitcherTrigger: "w-full justify-start text-sm py-1.5",
                    },
                  }}
                />
              </div>
              <div className="border-t pt-2.5 px-1 overflow-hidden">
                <UserButton afterSignOutUrl="/login" showName={true} appearance={{
                  elements: {
                    userButtonBox: "flex-row-reverse w-full",
                    userButtonOuterIdentifier: "text-sm truncate max-w-[140px]",
                  },
                }} />
              </div>
            </div>
          )}
        </aside>
      )}

      {/* Mobile sidebar overlay */}
      {!hideSidebar && mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-background/80"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-72 bg-background border-r shadow-xl flex flex-col">
            {/* Brand in mobile sidebar */}
            <div className="px-5 py-4 flex items-center justify-between flex-shrink-0">
              <span className="text-lg font-semibold">{t("components.layout.onsemblePro")}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
              {isInternal && (
                <>
                  <div className="pt-4 pb-2 px-4">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {t("components.layout.internal")}
                    </div>
                  </div>
                  {internalNavigationItems.map((item) => (
                    <NavLink
                      key={item.href}
                      href={item.href}
                      icon={item.icon}
                      label={item.label}
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  ))}
                </>
              )}
            </nav>

            {/* User section — pinned to bottom */}
            {isSignedIn && (
              <div className="flex-shrink-0 border-t px-3 py-3 space-y-2.5">
                <div className="px-1">
                  <OrganizationSwitcher
                    hidePersonal={true}
                    appearance={{
                      elements: {
                        rootBox: "w-full",
                        organizationSwitcherTrigger: "w-full justify-start text-sm py-1.5",
                      },
                    }}
                  />
                </div>
                <div className="border-t pt-2.5 px-1 overflow-hidden">
                  <UserButton afterSignOutUrl="/login" showName={true} appearance={{
                    elements: {
                      userButtonBox: "flex-row-reverse w-full",
                      userButtonOuterIdentifier: "text-sm truncate max-w-[140px]",
                    },
                  }} />
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Right column: header + main content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Header — scoped to main content area */}
        <Header>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hamburger}
            {/* Mobile brand — show when sidebar is hidden or no headerContent */}
            {(hideSidebar || !headerContent) && (
              <span className="lg:hidden text-sm font-semibold flex-shrink-0">{t("components.layout.onsemble")}</span>
            )}
            {headerContent}
          </div>
          {/* Right side of header */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
            {!hideSidebar && <HelpWidget />}
            {hideSidebar && isSignedIn && (
              <>
                <OrganizationSwitcher hidePersonal={true} />
                <UserButton afterSignOutUrl="/login" />
              </>
            )}
          </div>
        </Header>

        {/* Main content */}
        <main
          className={
            fillViewport
              ? "flex-1 bg-background p-4 md:p-6 flex flex-col min-h-0 min-w-0"
              : "flex-1 bg-background p-4 md:p-8 overflow-y-auto"
          }
        >
          {!isProfitCenter && <HajocaHealth />}
          <div
            className={
              fillViewport
                ? "max-w-[1800px] mx-auto flex-1 min-h-0 flex flex-col w-full"
                : "max-w-[1800px] mx-auto"
            }
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
